(() => {
  'use strict';
  const baseConfig = window.FAMILY_CIRCLE_AUTH;
  if (!baseConfig || !window.supabase) return;
  const workerAsset = document.querySelector('script[data-bbc-worker]')?.dataset.bbcWorker;
  const config = Object.freeze({ ...baseConfig, ...(workerAsset ? { pushWorkerUrl: new URL(workerAsset, location.origin).pathname } : {}) });
  window.FAMILY_CIRCLE_AUTH = config;
  const client = window.supabase.createClient(config.url, config.publishableKey, {
    auth: { flowType: 'pkce', persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
  });
  const returnKey = 'bbc-account-return-v2';
  const signInPath = '/pages/sign-in';
  let user = null;
  let completing = false;
  let version = 0;
  const listeners = new Set();
  function safeReturn(value) {
    try {
      const url = new URL(value || '/', location.origin);
      if (url.origin !== location.origin || url.username || url.password || url.pathname === signInPath || /^\/(?:account|checkout|checkouts)(?:\/|$)/.test(url.pathname)) return '/';
      for (const key of ['code', 'state', 'access_token', 'refresh_token', 'error', 'error_description']) url.searchParams.delete(key);
      return url.pathname + url.search + url.hash;
    } catch { return '/'; }
  }
  function rememberReturn(value) {
    const route = safeReturn(value);
    try { sessionStorage.setItem(returnKey, route); } catch {}
    return route;
  }
  function getReturn() {
    const query = new URL(location.href).searchParams.get('return_to');
    if (query) return rememberReturn(query);
    try { return safeReturn(sessionStorage.getItem(returnKey)); } catch { return '/'; }
  }
  function completeSignIn() {
    if (completing) return;
    completing = true;
    const route = getReturn();
    try { sessionStorage.removeItem(returnKey); sessionStorage.removeItem('bbc-sso-token-v1'); sessionStorage.removeItem('bbc-sso-pkce-pending-v1'); } catch {}
    location.replace(route);
  }
  function publish(next) {
    user = next;
    for (const listener of listeners) listener(user);
    window.dispatchEvent(new CustomEvent('bbc-account-change'));
  }
  async function refresh() {
    const attempt = ++version;
    try {
      const { data, error } = await client.auth.getUser();
      if (attempt === version) publish(error ? null : data?.user || null);
    } catch { if (attempt === version) publish(null); }
    return user;
  }
  function signIn() {
    const route = rememberReturn(location.pathname + location.search + location.hash);
    location.assign(signInPath + '?return_to=' + encodeURIComponent(route));
  }
  async function signOut() {
    await window.FAMILY_CIRCLE_PUSH?.removeThisDevice().catch(() => {});
    const { error } = await client.auth.signOut({ scope: 'local' });
    if (error) throw error;
    ++version;
    publish(null);
    try { sessionStorage.removeItem('bbc-sso-token-v1'); sessionStorage.removeItem('bbc-sso-pkce-pending-v1'); } catch {}
  }
  let resolveReady;
  let initialSessionResolved = false;
  const ready = new Promise(resolve => { resolveReady = resolve; });
  const initialSessionTimeout = setTimeout(() => {
    if (initialSessionResolved) return;
    void refresh().then(next => {
      if (initialSessionResolved) return;
      initialSessionResolved = true;
      resolveReady(next);
    });
  }, 12000);
  client.auth.onAuthStateChange((event, session) => {
    if (event === 'INITIAL_SESSION') {
      initialSessionResolved = true;
      clearTimeout(initialSessionTimeout);
      publish(session?.user || null);
      resolveReady(user);
    } else if (event === 'SIGNED_OUT') {
      ++version;
      publish(null);
      if (!initialSessionResolved) { initialSessionResolved = true; clearTimeout(initialSessionTimeout); resolveReady(null); }
    } else setTimeout(() => { void refresh(); }, 0);
  });
  window.BBC_ACCOUNT = Object.freeze({ client, ready, getUser: () => user, signIn, signOut, safeReturn,
    subscribe(listener) { listeners.add(listener); listener(user); return () => listeners.delete(listener); }
  });
  window.FAMILY_CIRCLE_SESSION = Object.freeze({ client, getUser: () => user });
  listeners.add(() => window.dispatchEvent(new CustomEvent('family-circle-auth')));
  const header = document.querySelector('.header__icons');
  const cart = document.getElementById('cart-icon-bubble');
  if (cart) { cart.hidden = true; cart.style.setProperty('display', 'none', 'important'); }
  if (header) {
    const action = document.createElement('button');
    action.type = 'button'; action.className = 'bbc-account-action'; action.disabled = true;
    action.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="8" r="3.5"></circle><path d="M4.5 20c.8-4 3.4-6 7.5-6s6.7 2 7.5 6"></path></svg>';
    header.append(action);
    const render = () => { action.setAttribute('aria-label', user ? 'Open your account' : 'Sign in to your account'); };
    listeners.add(render);
    ready.finally(() => { action.disabled = false; render(); });
    action.addEventListener('click', () => {
      if (!user) { if (location.pathname !== signInPath) signIn(); else document.getElementById('googleSignIn')?.focus(); return; }
      location.assign('/pages/profile');
    });
  }
  document.querySelectorAll('a[href]').forEach(link => {
    try {
      const url = new URL(link.href);
      if (url.hostname === 'circle.beginbabycare.com' && url.pathname === '/') {
        link.href = '/pages/family-circle'; link.removeAttribute('target');
      }
    } catch {}
  });
  const form = document.getElementById('bbc-sign-in');
  if (!form) return;
  const el = id => document.getElementById(id);
  let busy = false, initialized = false, email = '', name = '', resendAt = 0;
  const target = getReturn();
  el('accountReturnLink').href = target;
  function show(id) { form.querySelectorAll('.step-view').forEach(view => view.classList.toggle('active', view.id === id)); }
  function status(id, message) { el(id).textContent = message; }
  function controls(enabled = true) {
    ['googleSignIn', 'emailSubmit', 'verifySubmit'].forEach(id => el(id).disabled = !initialized || !enabled || busy);
    const seconds = Math.max(0, Math.ceil((resendAt - Date.now()) / 1000));
    el('resendCode').disabled = !initialized || !enabled || busy || !email || seconds > 0;
    el('resendCode').textContent = seconds ? `Send another code in ${seconds}s` : 'Send another code';
  }
  function errorMessage(error) {
    if (error?.status === 429) return 'Please wait a little before trying again.';
    if (/otp_expired|otp_disabled/.test(error?.code || '')) return 'That code is invalid or expired. Use your latest email or request another code.';
    return 'We couldn’t complete sign-in. Please try again.';
  }
  controls(false);
  const callback = new URL(location.href);
  const callbackError = callback.searchParams.has('error') || /error=/.test(callback.hash);
  ready.then(() => {
    initialized = true;
    if (user && !callbackError) { completeSignIn(); return; }
    status('authStatus', callbackError ? 'Google sign-in was not completed. Please try again.' : '');
    for (const key of ['code', 'state', 'error', 'error_description']) callback.searchParams.delete(key);
    history.replaceState(null, '', callback.pathname + callback.search);
    controls();
  });
  listeners.add(next => { if (next && !busy) completeSignIn(); });
  el('googleSignIn').addEventListener('click', async () => {
    if (busy) return;
    busy = true; controls(); status('authStatus', 'Opening Google sign-in…');
    try {
      rememberReturn(target);
      const { error } = await client.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: location.origin + signInPath } });
      if (error) throw error;
    } catch (error) { status('authStatus', errorMessage(error)); busy = false; controls(); }
  });
  async function sendCode(resend) {
    if (busy || (resend && Date.now() < resendAt)) return;
    const output = resend ? 'codeStatus' : 'authStatus';
    const nextEmail = resend ? email : el('authEmail').value.trim();
    const nextName = resend ? name : el('authName').value.trim();
    if (!nextEmail || !nextName) { status(output, 'Please enter your name and email address.'); return; }
    busy = true; controls(); status(output, 'Sending your code…');
    try {
      const { error } = await client.auth.signInWithOtp({ email: nextEmail, options: { shouldCreateUser: true, data: { full_name: nextName }, emailRedirectTo: location.origin + signInPath } });
      if (error) throw error;
      email = nextEmail; name = nextName; resendAt = Date.now() + 60000;
      el('emailCodeAddress').textContent = email; el('emailCode').value = '';
      status('authStatus', ''); status('codeStatus', 'Check your inbox and spam folder.'); show('emailCodeView'); el('emailCode').focus();
    } catch (error) { status(output, errorMessage(error)); }
    finally { busy = false; controls(); }
  }
  el('emailSignInForm').addEventListener('submit', event => { event.preventDefault(); void sendCode(false); });
  el('resendCode').addEventListener('click', () => { void sendCode(true); });
  form.querySelector('[data-view="authView"]').addEventListener('click', () => { if (!busy) show('authView'); });
  el('emailCodeForm').addEventListener('submit', async event => {
    event.preventDefault(); if (busy) return;
    const token = el('emailCode').value.trim();
    if (!email || !/^[0-9]{6,10}$/.test(token)) { status('codeStatus', 'Enter the code from your latest email.'); return; }
    busy = true; controls(); status('codeStatus', 'Checking your code…');
    try {
      const { error } = await client.auth.verifyOtp({ email, token, type: 'email' });
      if (error) throw error;
      await refresh();
      if (user) completeSignIn(); else status('codeStatus', 'We couldn’t confirm sign-in. Please try again.');
    } catch (error) { status('codeStatus', errorMessage(error)); }
    finally { busy = false; controls(); }
  });
  setInterval(() => controls(), 1000);
})();
